import { styled } from 'baseui'
import { belowBanner, viewportHeightBelowBanner } from '@/shared/theme/bannerHeight'
import { FLOATING_GAP } from '@/shared/theme/styled-components'
import {
  RAIL_EASE,
  RAIL_MOTION,
  RAIL_SPRING,
  railContentFadeIn,
  railIconPop,
  railStagger,
} from '@/shared/theme/railSlot'

const PANEL_TOGGLE_HEIGHT = '1.75rem'
const PANEL_TOGGLE_GAP = FLOATING_GAP

export const PANEL_HEADER_HEIGHT = `calc(${PANEL_TOGGLE_HEIGHT} + ${PANEL_TOGGLE_GAP})`

const panelRise = {
  from: { opacity: 0, transform: 'translateY(0.75rem) scale(0.98)' },
  to: { opacity: 1, transform: 'none' },
}

const toggleIconTurn = {
  from: { opacity: 0, transform: 'rotate(-90deg) scale(0.6)' },
  to: { opacity: 1, transform: 'none' },
}

// `:empty` matters: a module that renders nothing (wrong country, no eligibility) must not leave a gap.
export const SidebarBlock = styled<'div', { $plain?: boolean; $rail?: boolean; $highlighted?: boolean }>(
  'div',
  ({ $theme, $plain, $rail, $highlighted }) => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: $rail ? 'center' : 'flex-start',
    borderRadius: '1rem',
    ...($plain || $rail
      ? { backgroundColor: 'transparent' }
      : {
          backgroundColor: $theme.colors.white,
          // Highlighted: its blue header band runs to the edges, so a shadow replaces the outline.
          ...($highlighted
            ? { overflow: 'hidden', boxShadow: $theme.lighting.shadow400 }
            : { outline: `1px solid ${$theme.colors.borderPulseCard}`, outlineOffset: '-1px' }),
        }),
    transformOrigin: 'top center',
    animationName: $rail ? railIconPop : panelRise,
    ...railContentFadeIn,
    animationTimingFunction: $rail ? RAIL_SPRING : RAIL_EASE,
    ...railStagger(12, 35),
    ':empty': { display: 'none' },
  }),
)

// No display of its own, so the `hidden` attribute switches between the button and its rail icon.
export const CallbackSlot = styled('div', {
  width: '100%',
})

export const RightPanelColumn = styled<'div', { $expanded: boolean }>('div', ({ $theme, $expanded }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: $expanded ? 'flex-start' : 'center',
  gap: $expanded ? FLOATING_GAP : 0,
  flexGrow: 0,
  flexShrink: 0,
  boxSizing: 'border-box',
  width: $expanded ? '25%' : '3.5rem',
  paddingTop: FLOATING_GAP,
  paddingBottom: FLOATING_GAP,
  paddingLeft: $expanded ? FLOATING_GAP : 0,
  paddingRight: $expanded ? FLOATING_GAP : 0,
  backgroundColor: $expanded ? $theme.colors.bgPulseCanvas : $theme.colors.white,
  borderRadius: '0.75rem',
  transition: `width ${RAIL_MOTION}, padding ${RAIL_MOTION}, background-color ${RAIL_MOTION}`,
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  ...($expanded
    ? { position: 'relative' }
    : {
        position: 'sticky',
        top: belowBanner(FLOATING_GAP),
        maxHeight: viewportHeightBelowBanner(FLOATING_GAP),
        overflowY: 'auto',
      }),
}))

export const RightPanelToggleRow = styled<'div', { $expanded: boolean }>('div', ({ $expanded }) => ({
  flexShrink: 0,
  width: '100%',
  height: PANEL_TOGGLE_HEIGHT,
  marginBottom: $expanded ? 0 : '0.25rem',
}))

export const PanelToggleAnchor = styled('div', {
  position: 'absolute',
  top: FLOATING_GAP,
  right: '0.5rem',
  display: 'flex',
})

export const PanelToggleButton = styled<'button', { $expanded: boolean }>('button', ({ $theme, $expanded }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '2.5rem',
  height: PANEL_TOGGLE_HEIGHT,
  scrollMarginTop: `calc(2 * ${FLOATING_GAP})`,
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  paddingRight: 0,
  border: 0,
  borderRadius: '999px',
  backgroundColor: $expanded ? $theme.colors.white : $theme.colors.bg050,
  color: $theme.colors.primary,
  cursor: 'pointer',
  transitionProperty: 'background-color, transform',
  transitionDuration: '120ms',
  transitionTimingFunction: RAIL_EASE,
  ':hover': { backgroundColor: $theme.colors.bg150 },
  ':active': { transform: 'scale(0.92)' },
  ':focus-visible': { outline: `2px solid ${$theme.colors.primary}`, outlineOffset: '2px' },
}))

export const PanelToggleIcon = styled('span', {
  display: 'inline-flex',
  animationName: toggleIconTurn,
  animationDuration: '360ms',
  animationTimingFunction: RAIL_SPRING,
  '@media (prefers-reduced-motion: reduce)': { animationName: 'none' },
})
